import type { SupportedLocales } from "#environment/i18nConfig"
import type { Artist } from "#models/artist"
import type { Artwork } from "#models/artwork"
import type { Category } from "#models/category"
import type { Media } from "#models/media"
import type { IFilter } from "#modules/marketplace/filtering/factory/filter/IFilter"
import type { CollectionResponse, Meta, Pagination } from "#utils/meta"

import { cache } from "react"
import { unstable_cache } from "next/cache"
import axios from "axios"

import { ENVIRONMENT } from "#environment/env"
import { api } from "#lib/http-client"
import { toData } from "#utils/axiosUtils"
import { flattenAttributes, generateQueryString, paginationString } from "#utils/requestUtils"

/**
 * TODO ALLES IN DIESEM SERVICE REFACTOREN -- SEHR SCHLECHTER CODE
 * dont use class anymore, instead use object
 */

class ArtworkService {
  addArtwork(
    name: string,
    price: number,
    previewImages: (number | undefined)[],
    sold: boolean,
    artist: number,
    width: number,
    height: number,
    year: string,
    description: string,
    technique: string,
    category: string,
    style: string,
  ): Promise<any> {
    const requestData = JSON.stringify({
      data: {
        name: name,
        price: price,
        previewImages: previewImages,
        sold: sold,
        artist: artist,
        width: width,
        height: height,
        year: year,
        description: description,
        technique: "",
        category: "",
        style: "",
      },
    })

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: ENVIRONMENT.NEXT_PUBLIC_API_URL + "/artworks",
      headers: {
        "Content-Type": "application/json",
      },
      data: requestData,
    }

    return axios.request(config)
  }

  async getArtworkSlugs({ page, pageSize, locale }: { page: number; pageSize: number; locale: string }): Promise<{
    meta: { pagination: Pagination }
    data: { id: number; attributes: { slug: string } }[]
  }> {
    // TODO remove when in payload
    if (locale === "en") {
      locale = "de-DE"
    } else locale = "en"

    // TODO other client than axios that is based on fetch client to support NextJS native fetch cache etc
    return await api()
      .get(`/artworks?${paginationString(page, pageSize, true)}&fields=slug&locale=${locale}`)
      .then(toData)
  }

  async get(filter: IFilter[], page: number, pageSize: number, includeCount = true): Promise<Meta<Pagination, Artwork[]>> {
    return this.getFromString(this.createFilterRequestString(filter, page, pageSize, includeCount))
  }

  async cached_get(filter: IFilter[], page: number, pageSize: number, includeCount = true): Promise<Meta<Pagination, Artwork[]>> {
    return this.cached_getFromString(this.createFilterRequestString(filter, page, pageSize, includeCount))
  }

  createFilterRequestString(filter: IFilter[], page: number, pageSize: number, includeCount = true): string {
    // TODO alles auf URLSearchParams ändern anstatt manuell String manipulieren ODER OpenAPI client verwenden
    let filterRequestString = generateQueryString(filter)
    if (filterRequestString.length !== 0) filterRequestString = "&" + filterRequestString
    return `/artworks?${paginationString(page, pageSize, includeCount)}&populate=*${filterRequestString}`
  }

  // filters${this.buildKeyArray()}[$in][${i}]=${this.values[i]}`

  async getFromString(requestString: string) {
    const response = await api().get(requestString)
    const meta: Pagination = response.data.meta.pagination
    let artworks: Artwork[] = response.data.data.map(convertStrapiArtwork)
    // TODO fix workaround when payload
    artworks = artworks.map(artwork => {
      //@ts-ignore
      artwork.localizedSlugs = {
        de: artwork.slug,
        //@ts-ignore
        en: artwork.localizations?.data?.[0]?.attributes?.slug,
      }
      return artwork
    })
    return { meta: meta, content: artworks }
  }

  cached_getFromString = unstable_cache(this.getFromString, [], { tags: ["marketplace_artworks"] })

  public getIdFromSlug = getIdFromSlug

  async findById(id: number, locale: string): Promise<Artwork | undefined> {
    // TODO hier irgendeine Form von Error Handling oder alles in der error.tsx boundary?
    //  -> hier error irgendwie auswerten, zu einer entsprechenden Message transformieren,
    //  neuen Error mit der Message thrown und dann nur noch in Error.tsx component anzeigen lassen?
    try {
      const response = await api()
        .get(`/artworks/${id}?populate[0]=previewImages&populate[1]=artist.profilePicture&populate[2]=artist.fullBodyPicture&populate[3]=localizations`)
        .then(toData)

      const artwork = convertStrapiArtwork(response.data)

      if (artwork) {
        // TODO fix workaround when payload
        //@ts-ignore
        artwork.localizedSlugs = {
          de: artwork.slug,
          //@ts-ignore
          en: artwork.localizations?.data?.[0]?.attributes?.slug,
        }
      }

      // TODO REMOVE THIS ABSOLUTE SHIT CODE WHEN USING PAYLOAD - strapi is simply shit in localization
      if (locale === "en") {
        //@ts-ignore
        if (artwork.localizations?.data?.[0]?.attributes?.slug) {
          //@ts-ignore
          artwork.slug = artwork.localizations?.data?.[0]?.attributes?.slug
        }

        //@ts-ignore
        if (artwork.localizations?.data?.[0]?.attributes?.description) {
          //@ts-ignore
          artwork.description = artwork.localizations?.data?.[0]?.attributes?.description
        }

        //@ts-ignore
        if (artwork.localizations?.data?.[0]?.attributes?.style) {
          //@ts-ignore
          artwork.style = artwork.localizations?.data?.[0]?.attributes?.style
        }

        //@ts-ignore
        if (artwork.localizations?.data?.[0]?.attributes?.technique) {
          //@ts-ignore
          artwork.technique = artwork.localizations?.data?.[0]?.attributes?.technique
        }
      }

      return artwork
    } catch (e) {}

    return undefined
  }

  async getArtworksForArtist(artistID: number, limit: number): Promise<Artwork[]> {
    try {
      const response = await api().get(`/artworks?${paginationString(1, limit, false)}&filters[artist]=${artistID}&populate[0]=previewImages`)

      return response.data.data.map((artwork: any) => convertStrapiArtwork(artwork))
    } catch (e) {}

    return []
  }

  // TODO remove when using better fetch client that uses the nextjs auto caching
  public cached_findById: (id: number, locale: SupportedLocales) => Promise<Artwork | undefined> = cache(this.findById)

  public convertStrapiArtwork(strapiObjectResponse: any): Artwork {
    const artwork: Artwork = flattenAttributes<Artwork>(strapiObjectResponse)
    artwork.previewImages = strapiObjectResponse.attributes.previewImages?.data.map(flattenAttributes<Media>)
    artwork.artist = flattenAttributes<Artist>(strapiObjectResponse.attributes.artist?.data)

    // TODO einfach schlechte herangehensweise
    if (strapiObjectResponse.attributes.artist?.profilePicture) {
      artwork.artist.profilePicture = flattenAttributes<Media>(strapiObjectResponse.attributes.artist?.profilePicture?.data)
    }
    if (strapiObjectResponse.attributes.artist?.fullBodyPicture) {
      artwork.artist.fullBodyPicture = flattenAttributes<Media>(strapiObjectResponse.attributes.artist?.fullBodyPicture?.data)
    }
    artwork.category = flattenAttributes<Category>(strapiObjectResponse.attributes.category?.data)
    return artwork
  }

  //@deprecated
  async findByArtist(artistId: number, page: number, pageSize: number): Promise<CollectionResponse<Artwork>> {
    const response = await api().get(`/artworks?filters[artist][id][$eq]=${artistId}&populate=previewImages&${paginationString(page, pageSize, true)}`)
    //`/artists/${artistId}?populate[0]=artworks.previewImages&${paginationString(page, pageSize, true)}`

    return { ...response.data, data: response.data.data.map((a: any) => this.convertStrapiArtwork(a)) }
  }
}

function convertStrapiArtwork(strapiObjectResponse: any): Artwork {
  const artwork: Artwork = flattenAttributes<Artwork>(strapiObjectResponse)
  artwork.previewImages = strapiObjectResponse.attributes.previewImages?.data.map(flattenAttributes<Media>)
  artwork.artist = flattenAttributes<Artist>(strapiObjectResponse.attributes.artist?.data)

  // TODO einfach schlechte herangehensweise
  if (strapiObjectResponse.attributes.artist?.profilePicture) {
    artwork.artist.profilePicture = flattenAttributes<Media>(strapiObjectResponse.attributes.artist?.profilePicture?.data)
  }
  if (strapiObjectResponse.attributes.artist?.fullBodyPicture) {
    artwork.artist.fullBodyPicture = flattenAttributes<Media>(strapiObjectResponse.attributes.artist?.fullBodyPicture?.data)
  }
  artwork.category = flattenAttributes<Category>(strapiObjectResponse.attributes.category?.data)
  return artwork
}

// TODO move into service correctly
function getIdFromSlug(slug: string): number | undefined {
  try {
    let idCandidate = slug.split("-").at(-1)
    if (!idCandidate) idCandidate = slug

    const id = Number(idCandidate)
    if (Number.isNaN(id)) return undefined

    return id
  } catch (e) {
    return undefined
  }
}

export const artworkService = new ArtworkService()

async function findBySlug(slug: string, locale: SupportedLocales): Promise<Artwork | undefined> {
  const id = getIdFromSlug(slug)
  if (!id) return undefined

  return await artworkService.cached_findById(id, locale)
}

export const cached_findBySlug: (slug: string, locale: SupportedLocales) => Promise<Artwork | undefined> = cache(findBySlug)
